import React, { useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SettingsIcon from "@mui/icons-material/Settings";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const toolCategories = {
  "Image Tools": [
    { name: "QR Code Generator", path: "/qr-code-generator" },
    { name: "Image Trim", path: "/image-trim" },
    { name: "Image Resizer", path: "/image-resizer" },
  ],
  "Design Tools": [
    { name: "Font Selector", path: "/font-selector" },
    { name: "Sign Maker", path: "/sign-maker" },
  ],
  Apps: [{ name: "Mac Apps", path: "/apps" }],
  "Business Tools": [
    { name: "Project Matrix", path: "/project-matrix" },
    { name: "Security Tools", path: "/security-tools" },
    { name: "Prompt Generator", path: "/prompt-generator" },
    { name: "Qbert", path: "/qbert" },
    { name: "CertSprint", path: "/cert-sprint" },
    { name: "Web Stats", path: "/web-stats" },
    { name: "Script Downloader", path: "/script-downloader" },
    { name: "Expense Tracker", path: "/expense-tracker" },
  ],
};

function Navbar() {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElDropdown, setAnchorElDropdown] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [toolsPerRow, setToolsPerRow] = useState(3);

  const { isLoading, isAuthenticated, logout, userAttributes } = useAuth();

  useEffect(() => {
    // Load settings from localStorage
    const savedToolsPerRow = localStorage.getItem("toolsPerRow");
    if (savedToolsPerRow) {
      const parsedValue = parseInt(savedToolsPerRow, 10);
      if (parsedValue >= 2 && parsedValue <= 6) {
        setToolsPerRow(parsedValue);
      }
    }
  }, []);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleOpenDropdown = (
    category: string,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    setAnchorElDropdown((prev) => ({
      ...prev,
      [category]: event.currentTarget,
    }));
  };

  const handleCloseDropdown = (category: string) => {
    setAnchorElDropdown((prev) => ({
      ...prev,
      [category]: null,
    }));
  };

  const handleCloseAllDropdowns = () => {
    setAnchorElDropdown({});
  };

  const handleOpenSettings = () => {
    setSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setSettingsOpen(false);
  };

  const handleToolsPerRowChange = (event: SelectChangeEvent<number>) => {
    const newValue = event.target.value as number;
    setToolsPerRow(newValue);
    localStorage.setItem("toolsPerRow", newValue.toString());

    // Dispatch custom event to notify other components
    window.dispatchEvent(
      new CustomEvent("settingsChanged", {
        detail: { toolsPerRow: newValue },
      }),
    );
  };

  return (
    <>
      <AppBar position="static">
        <Container maxWidth="xl">
          <Toolbar disableGutters variant="dense">
            <Typography
              variant="h6"
              noWrap
              component={RouterLink}
              to="/"
              sx={{
                mr: 2,
                display: { xs: "none", md: "flex" },
                fontFamily: "monospace",
                fontWeight: 700,
                letterSpacing: ".3rem",
                color: "inherit",
                textDecoration: "none",
              }}
            >
              Sean's Utils
            </Typography>

            {/* Mobile Menu */}
            <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
                data-testid="navbar-mobile-menu-button"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: "block", md: "none" },
                }}
              >
                {Object.entries(toolCategories)
                  .map(([category, tools], categoryIndex) => [
                    <MenuItem key={`${category}-header`} disabled>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", color: "primary.main" }}
                      >
                        {category}
                      </Typography>
                    </MenuItem>,
                    ...tools.map((tool) => (
                      <MenuItem
                        key={tool.path}
                        onClick={handleCloseNavMenu}
                        component={RouterLink}
                        nativeButton={false}
                        to={tool.path}
                        data-testid={`navbar-mobile-tool-${tool.path}`}
                      >
                        <Typography
                          sx={{
                            textAlign: "center",
                            pl: 2,
                          }}
                        >
                          {tool.name}
                        </Typography>
                      </MenuItem>
                    )),
                    categoryIndex < Object.keys(toolCategories).length - 1 ? (
                      <Divider key={`${category}-divider`} />
                    ) : null,
                  ])
                  .flat()
                  .filter(Boolean)}
              </Menu>
            </Box>

            <Typography
              variant="h5"
              noWrap
              component={RouterLink}
              to="/"
              sx={{
                mr: 2,
                display: { xs: "flex", md: "none" },
                flexGrow: 1,
                fontFamily: "monospace",
                fontWeight: 700,
                letterSpacing: ".3rem",
                color: "inherit",
                textDecoration: "none",
              }}
            >
              Sean's Utils
            </Typography>

            {/* Desktop Menu with Dropdowns */}
            <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
              {Object.entries(toolCategories).map(([category, tools]) => (
                <Box key={category}>
                  <Button
                    onClick={(e) => handleOpenDropdown(category, e)}
                    sx={{ color: "white", textTransform: "none" }}
                    endIcon={<ArrowDropDownIcon />}
                    data-testid={`navbar-category-${category}`}
                  >
                    {category}
                  </Button>
                  <Menu
                    anchorEl={anchorElDropdown[category]}
                    open={Boolean(anchorElDropdown[category])}
                    onClose={() => handleCloseDropdown(category)}
                    slotProps={{
                      list: {
                        "aria-labelledby": "basic-button",
                      },
                    }}
                  >
                    {tools.map((tool) => (
                      <MenuItem
                        key={tool.path}
                        onClick={() => {
                          handleCloseDropdown(category);
                          handleCloseAllDropdowns();
                        }}
                        component={RouterLink}
                        nativeButton={false}
                        to={tool.path}
                        data-testid={`navbar-tool-${tool.path}`}
                      >
                        {tool.name}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              ))}
            </Box>

            {/* Settings and Auth Icons */}
            <Box
              sx={{
                flexGrow: 0,
                display: "flex",
                alignItems: "center",
                flexWrap: "nowrap",
                gap: 1,
              }}
            >
              {/* Authentication Section */}
              {isLoading ? (
                <CircularProgress
                  size={24}
                  color="inherit"
                  aria-label="Checking sign-in status"
                />
              ) : isAuthenticated && userAttributes ? (
                <>
                  <Box
                    component={RouterLink}
                    to="/account"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "inherit",
                      textDecoration: "none",
                      "&:hover": { opacity: 0.85 },
                    }}
                    title="Account settings (passkeys)"
                  >
                    <AccountCircleIcon />
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ display: { xs: "none", sm: "inline" } }}
                    >
                      Welcome {userAttributes.given_name || "User"}
                    </Typography>
                  </Box>
                  <Button
                    color="inherit"
                    onClick={logout}
                    startIcon={<LogoutIcon />}
                    aria-label="Logout"
                    sx={{ ml: 1, whiteSpace: "nowrap" }}
                  >
                    <Box
                      component="span"
                      sx={{ display: { xs: "none", sm: "inline" } }}
                    >
                      Logout
                    </Box>
                  </Button>
                </>
              ) : (
                <Button
                  color="inherit"
                  component={RouterLink}
                  to="/auth/login"
                  startIcon={<LoginIcon />}
                  aria-label="Login"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  <Box
                    component="span"
                    sx={{ display: { xs: "none", sm: "inline" } }}
                  >
                    Login
                  </Box>
                </Button>
              )}

              {/* Settings Icon */}
              <IconButton
                size="large"
                aria-label="settings"
                onClick={handleOpenSettings}
                color="inherit"
              >
                <SettingsIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      {/* Settings Modal */}
      <Dialog
        open={settingsOpen}
        onClose={handleCloseSettings}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="tools-per-row-label">Tools per Row</InputLabel>
              <Select
                labelId="tools-per-row-label"
                id="tools-per-row-select"
                value={toolsPerRow}
                label="Tools per Row"
                onChange={handleToolsPerRowChange}
              >
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3</MenuItem>
                <MenuItem value={4}>4</MenuItem>
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={6}>6</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Controls how many tools are displayed per row on larger screens.
              Default is 3.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettings}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Navbar;
